/** @jsxImportSource @emotion/react */

import { Fragment } from 'react'
import css from '@emotion/css/macro'
import styled from '@emotion/styled/macro'
import isPropValid from '@emotion/is-prop-valid'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'
import { ROUTES } from '@sportninja/common/constants/app'
import colors from '@sportninja/common/constants/appColors'
import { paragraphXSmall, weightMedium } from '../css'
import HomeIcon from '../HomeIcon'
import Text from '../ts-components/Text'

/**
 * ArrowRight component
 * Renders a right-pointing arrow SVG icon for breadcrumb separators.
 */
const ArrowRight = ({ color = colors.NEUTRAL_50 }) => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='16'
    height='16'
    viewBox='0 0 16 16'
    fill='none'
  >
    <g clipPath='url(#clip0_6717_844)'>
      <path
        d='M5.72656 11.06L8.7799 8L5.72656 4.94L6.66656 4L10.6666 8L6.66656 12L5.72656 11.06Z'
        fill={color}
      />
    </g>
    <defs>
      <clipPath id='clip0_6717_844'>
        <rect width='16' height='16' fill={color} />
      </clipPath>
    </defs>
  </svg>
)

/**
 * ScheduleLink styled component
 * Styled link component for breadcrumb navigation items.
 */
const ScheduleLink = styled('div', {
  shouldForwardProp: (prop) => isPropValid(prop),
})`
  ${paragraphXSmall};
  ${weightMedium}
  color: ${colors.PRIMARY};
  text-transform: uppercase;
`

/**
 * ScheduleBreadcrumbs component
 * Renders the "Home › Org › Schedule › (Sub-Schedule) › Game #" breadcrumb trail.
 *
 * @param {Object} props
 * @param {Object} props.organization - Organization object with id and name
 * @param {Object} props.game - Game object with schedule and parent information
 * @param {string} props.title - The final breadcrumb title (e.g., "Game #123")
 * @param {boolean} props.isDesktop - Whether rendering on desktop view
 * @param {string} props.className - Optional additional CSS class
 */
export const ScheduleBreadcrumbs = ({
  organization,
  game,
  title,
  isDesktop,
  className,
}) => {
  return (
    <div
      css={css`
        margin: 8px 0px;
        ${isDesktop
          ? css`
              min-height: 14px;
            `
          : css`
              text-align: center;
            `}
      `}
      className={className}
    >
      {game.rootSchedule && (
        <div
          css={css`
            display: flex;
            align-items: center;
          `}
        >
          <Link to={ROUTES.HOME}>
            <HomeIcon width={14} height={14} fill={colors.PALE_SKY} />
          </Link>
          <ArrowRight />
          <ScheduleLink
            as={Link}
            isDesktop={isDesktop}
            to={`${ROUTES.ORG_ROOT}/${organization?.id}`}
          >
            {organization && organization?.name ? (
              <Fragment>{organization.name}</Fragment>
            ) : null}
          </ScheduleLink>
          <ArrowRight />
          <ScheduleLink
            as={Link}
            isDesktop={isDesktop}
            to={`${ROUTES.SCHEDULE_ROOT}/${game.rootSchedule.id}`}
          >
            {game.rootSchedule.name_full || game.rootSchedule.name}
          </ScheduleLink>
          {game.parentId !== game?.rootSchedule?.id && (
            <>
              <ArrowRight />
              <ScheduleLink
                as={Link}
                isDesktop={isDesktop}
                to={`${ROUTES.SCHEDULE_ROOT}/${game.rootSchedule.id}?scheduleType=${game.parentScheduleType}&schedule_id=${game.parentId}`}
              >
                {game.parentSchedule}
              </ScheduleLink>
            </>
          )}
          <>
            <ArrowRight />
            <Text
              variant='paragraphXSmall'
              weight='semiBold'
              color={colors.NEUTRAL_50}
              style={`
                text-transform: uppercase;
              `}
            >
              {title}
            </Text>
          </>
        </div>
      )}
    </div>
  )
}

ScheduleBreadcrumbs.propTypes = {
  organization: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  game: PropTypes.any.isRequired,
  title: PropTypes.string.isRequired,
  isDesktop: PropTypes.bool.isRequired,
  className: PropTypes.string,
}

export default ScheduleBreadcrumbs
